import { useRef, useState, useEffect } from 'react';
import ColorPickerWrapper from './ColorPickerWrapper';
import { DEFAULT_HEX_COLOR } from './constants';
import Fab from '@mui/material/Fab';
import Popover from '@mui/material/Popover';
import ChatBotSpaceHolder from 'components/editor/SidePanel/component/ChatBotSpaceHolder';
import type { colorParams } from './types';

export type ColorResult = string;

interface ColorPickerProps {
  handleColorChange: (color: ColorResult) => void;
  handleOnClose?: (color: ColorResult) => void;
  currentColor?: ColorResult;
  customRef?: React.RefObject<HTMLElement>;
  onOpen?: () => void;
  presetColors?: colorParams[];
}

interface ColorPopupProps {
  onClose?: (color: ColorResult, isValueSet: boolean) => void;
  open: boolean;
  useRgba?: boolean;
  buttonRef?: React.RefObject<HTMLElement>;
  currentColor: ColorResult;
  onColorChange?: (color: ColorResult) => void;
  presetColors?: colorParams[];
}

export function ColorPopup({ onClose, open, buttonRef, currentColor, onColorChange, presetColors }: ColorPopupProps) {
  const [color, setColor] = useState<ColorResult>(currentColor);

  useEffect(() => {
    setColor(currentColor);
  }, [currentColor]);

  const defaultWidthInPx = 250;

  const handleClose = (e?: KeyboardEvent) => {
    const isColorApplied = e?.key !== 'Escape';
    onClose?.(color, isColorApplied);
  };

  const colorChangeHandler = (color: ColorResult) => {
    setColor(color);
    onColorChange?.(color);
  };

  return (
    <Popover
      disableScrollLock={false}
      onClose={(e: KeyboardEvent) => handleClose(e)}
      open={open}
      anchorEl={buttonRef?.current}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        },
      }}
    >
      <div className="color-picker-container">
        <ColorPickerWrapper
          color={currentColor}
          width={defaultWidthInPx}
          onChange={(newColor) => colorChangeHandler(newColor)}
          presetColors={presetColors}
        />
        <ChatBotSpaceHolder bgcolor="transparent" onClick={() => handleClose()} />
      </div>
    </Popover>
  );
}

export default function ColorPicker({
  handleColorChange,
  customRef,
  handleOnClose,
  currentColor = DEFAULT_HEX_COLOR,
  onOpen,
  presetColors,
}: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<ColorResult>(currentColor);

  useEffect(() => {
    setSelectedColor(currentColor);
  }, [currentColor]);

  const fabRef = useRef(null);

  useEffect(() => {
    if (currentColor && currentColor !== selectedColor) {
      setSelectedColor(currentColor);
    }
  }, [currentColor]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (color: ColorResult) => {
    setOpen(false);
    handleOnClose?.(color);
  };

  const handleColorChangeHandler = (color: ColorResult) => {
    setSelectedColor(color);
    handleColorChange(color);
  };

  useEffect(() => {
    if (open) {
      onOpen?.();
    }
  }, [open]);

  const buttonStyle = {
    width: '32px',
    height: '32px',
    minHeight: '32px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    boxShadow: 'none',
    border: '1px solid rgba(189, 189, 189, 0.6)',
    '&:hover': {
      transform: 'scale(1.1)',
      filter: 'brightness(95%)',
      boxShadow: 'none',
    },
    '&:active': {
      boxShadow: 'none',
    },
  };

  return (
    <>
      <Fab sx={[buttonStyle]} ref={fabRef} style={{ backgroundColor: selectedColor }} onClick={handleClickOpen}></Fab>
      <ColorPopup
        open={open}
        onClose={handleClose}
        buttonRef={customRef || fabRef}
        currentColor={selectedColor}
        onColorChange={handleColorChangeHandler}
        presetColors={presetColors}
      />
    </>
  );
}
